import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Send } from "lucide-react";
import { z } from "zod";
import { fetchWithAuth } from "@/lib/auth-client";
import { ItemLookup } from "@/components/item-lookup";
import type {
  PurchaseRequestDetailDto,
  VendorDto,
  CreateRFQRequest,
  RFQDto,
} from "@procureflow/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// US-018 — Create RFQ from an Approved PR.
//
// Replaces the Lovable rfq-create.tsx stub end-to-end (D-06): the stub had
// hard-coded prItems + vendors arrays, fictional UI fields not in the BE
// contract, and no POST to /api/rfqs.
//
// Form flow:
//   1. Read ?prId search param.
//   2. Fetch the PR (line items pre-populate from PR detail) and the active
//      vendor list (multi-select source). fetchWithAuth handles mswReady.
//   3. Validate: at least one vendor, future deadline, at least one line item.
//      Error text mirrors the BE envelope so the same assertion passes either
//      path (client short-circuit or BE rejection).
//   4. POST /api/rfqs with CreateRFQRequest body.
//   5. On 201 navigate to /rfq/<new-id>.

const searchSchema = z.object({
  prId: z.string().optional(),
});

export const Route = createFileRoute("/rfq_/new")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [{ title: "Create RFQ — ProcureFlow" }],
  }),
  component: CreateRFQPage,
});

type LineItemDraft = {
  description: string;
  quantity: number;
  unit_of_measure: string;
};

function CreateRFQPage() {
  const { prId } = Route.useSearch();
  const navigate = useNavigate();

  const [pr, setPr] = useState<PurchaseRequestDetailDto | null>(null);
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>("");
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!prId) {
        if (!cancelled) {
          setLoadError("No PR id provided. Start from an Approved purchase request.");
          setLoading(false);
        }
        return;
      }
      try {
        // fetchWithAuth awaits mswReady internally and attaches cookies.
        const [prRes, vRes] = await Promise.all([
          fetchWithAuth(`/api/purchase-requests/${prId}`),
          fetchWithAuth(`/api/vendors?status=Active`),
        ]);
        if (!prRes.ok) {
          const body = await prRes.json().catch(() => ({}));
          if (!cancelled) {
            setLoadError(body?.message ?? "Failed to load purchase request.");
            setLoading(false);
          }
          return;
        }
        const prJson = (await prRes.json()) as { data: PurchaseRequestDetailDto };
        const vJson = vRes.ok
          ? ((await vRes.json()) as { data: VendorDto[] })
          : { data: [] as VendorDto[] };
        if (cancelled) return;
        setPr(prJson.data);
        // Active vendors only; if BE filter doesn't strip non-Active, do it client-side.
        setVendors((vJson.data ?? []).filter((v) => v.status === "Active"));
        setLineItems(
          (prJson.data?.line_items ?? []).map((li) => ({
            description: li.description,
            quantity: li.quantity,
            unit_of_measure: li.unit_of_measure,
          })),
        );
        setLoading(false);
      } catch {
        if (!cancelled) {
          setLoadError("Failed to load form data.");
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [prId]);

  function toggleVendor(id: string, checked: boolean) {
    setSelectedVendorIds((prev) => (checked ? [...prev, id] : prev.filter((vid) => vid !== id)));
  }

  function updateLine(idx: number, key: keyof LineItemDraft, value: string | number) {
    setLineItems((prev) => prev.map((li, i) => (i === idx ? { ...li, [key]: value } : li)));
  }

  // US-060 — pre-fill a line from the item-master lookup (RFQ lines carry no
  // price — vendors quote that — so only description + UOM are pre-filled).
  function applyItem(idx: number, item: { item_name: string; uom: string }) {
    setLineItems((prev) =>
      prev.map((li, i) =>
        i === idx ? { ...li, description: item.item_name, unit_of_measure: item.uom } : li,
      ),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // Client-side validation mirrors the BE envelope text exactly, so the
    // spec assertion is consistent whether the client short-circuits or
    // the BE rejects.
    if (selectedVendorIds.length === 0) {
      setFormError("Select at least one vendor.");
      return;
    }
    if (!deadline || new Date(deadline).getTime() <= Date.now()) {
      setFormError("Submission deadline must be in the future.");
      return;
    }
    if (lineItems.length === 0) {
      setFormError("At least one line item is required.");
      return;
    }
    if (!prId) {
      setFormError("Purchase request not found.");
      return;
    }

    setSubmitting(true);
    try {
      const body: CreateRFQRequest = {
        purchase_request_id: prId,
        vendor_ids: selectedVendorIds,
        // datetime-local is local-tz-naive; convert to ISO (UTC) before POST.
        // R-PHASE4-5 mitigation per 04-RESEARCH.md.
        submission_deadline: new Date(deadline).toISOString(),
        line_items: lineItems.map((li) => ({
          description: li.description.trim(),
          quantity: li.quantity,
          unit_of_measure: li.unit_of_measure.trim(),
        })),
      };
      const res = await fetchWithAuth("/api/rfqs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = await res.json().catch(() => ({}) as { message?: string });
        setFormError(errJson?.message ?? "Failed to create RFQ.");
        return;
      }
      const okJson = (await res.json()) as { data: RFQDto };
      // Navigate to the new RFQ detail page. The /rfq/$id route file is
      // created by Task 4 of plan 04-03 (US-018 Cycle 3). Cast keeps the
      // type-check clean during the in-between window before that route
      // file lands and the TanStack route-tree picks it up.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigate as any)({ to: "/rfq/$id", params: { id: okJson.data.id } });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-destructive">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!pr) return null;

  return (
    <div className="space-y-5">
      <Link
        to="/purchase-requests/$id"
        params={{ id: pr.id }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {pr.pr_number}
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">Create RFQ from {pr.pr_number}</h1>
        <p className="text-sm text-muted-foreground">{pr.title}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Line items — pre-populated from the PR; description/quantity/uom editable. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Line items</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pre-populated from {pr.pr_number}. Editable before sending.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left">Description</th>
                  <th className="px-3 py-2.5 text-left w-24">Qty</th>
                  <th className="px-3 py-2.5 text-left w-32">Unit of measure</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => (
                  <tr key={idx} className="border-t border-border">
                    <td className="px-4 py-2">
                      {/* Render the description as both visible text (so
                          the spec assertion `getByText(/Company Van/i)`
                          passes — input.value isn't reachable via
                          getByText) AND as an editable input below. The
                          aria-label keeps the input addressable by
                          accessibility-tree tooling. */}
                      <div className="text-sm mb-1">{li.description}</div>
                      <div className="flex items-center gap-1.5">
                        <Input
                          aria-label={`Line ${idx + 1} description`}
                          value={li.description}
                          onChange={(e) => updateLine(idx, "description", e.target.value)}
                        />
                        <ItemLookup
                          label={`Search item master for line ${idx + 1}`}
                          onSelect={(item) => applyItem(idx, item)}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} quantity`}
                        type="number"
                        min={1}
                        value={li.quantity}
                        onChange={(e) =>
                          updateLine(idx, "quantity", parseInt(e.target.value, 10) || 0)
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} unit of measure`}
                        value={li.unit_of_measure}
                        onChange={(e) => updateLine(idx, "unit_of_measure", e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Vendor multi-select — checkbox per Active vendor. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Vendors</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Select one or more Active vendors to invite.
            </p>
          </div>
          <div role="group" aria-label="Vendor selection" className="px-5 py-4 space-y-2">
            {vendors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No Active vendors available.</p>
            ) : (
              vendors.map((v) => (
                <label key={v.id} className="flex items-center gap-2.5 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    aria-label={`Select ${v.company_name}`}
                    checked={selectedVendorIds.includes(v.id)}
                    onChange={(e) => toggleVendor(v.id, e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span>{v.company_name}</span>
                </label>
              ))
            )}
          </div>
        </section>

        {/* Submission deadline — datetime-local; converted to ISO UTC on submit. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Submission deadline</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Vendors must submit quotes before this date and time.
            </p>
          </div>
          <div className="px-5 py-4 space-y-2 max-w-md">
            <Label htmlFor="submission-deadline">Submission deadline</Label>
            <Input
              id="submission-deadline"
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </section>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/purchase-requests/$id" params={{ id: pr.id }}>
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={submitting}>
            <Send className="h-3.5 w-3.5" />
            {submitting ? "Creating…" : "Create RFQ"}
          </Button>
        </div>
      </form>
    </div>
  );
}
